import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Upload, CheckCircle2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface TestSubmissionConfirmationDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  pendingUploadsCount?: number;
  failedUploadsCount?: number;
  hasVideoResponses?: boolean;
}

export function TestSubmissionConfirmationDialog({
  open,
  onConfirm,
  onCancel,
  pendingUploadsCount = 0,
  failedUploadsCount = 0,
  hasVideoResponses = false,
}: TestSubmissionConfirmationDialogProps) {
  const hasPendingUploads = pendingUploadsCount > 0;
  const hasFailures = failedUploadsCount > 0;
  const canSubmit = !hasPendingUploads; // Can submit even with failures (user's choice)

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
              hasPendingUploads 
                ? 'bg-blue-500/10' 
                : 'bg-orange-500/10'
            }`}>
              {hasPendingUploads ? (
                <Upload className="h-6 w-6 text-blue-500 animate-pulse" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-orange-500" />
              )}
            </div>
            <AlertDialogTitle className="text-xl">
              {hasPendingUploads ? "Finalizing Responses..." : "Submit Test?"}
            </AlertDialogTitle>
          </div>
          
          {hasPendingUploads ? (
            <div className="space-y-3 pt-2">
              <AlertDialogDescription className="text-base">
                Your video responses are being uploaded in the background. 
                Please wait for all uploads to complete before submitting your test.
              </AlertDialogDescription>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {pendingUploadsCount} {pendingUploadsCount === 1 ? 'upload' : 'uploads'} remaining
                  </span>
                  <Upload className="h-4 w-4 text-blue-500 animate-pulse" />
                </div>
                <Progress value={undefined} className="h-2" indicatorClassName="bg-blue-500" />
              </div>
              
              <p className="text-xs text-muted-foreground">
                The "Submit Test" button will be enabled once all uploads are complete.
              </p>
            </div>
          ) : (
            <div className="space-y-3 pt-2">
              <AlertDialogDescription className="text-base">
                Are you sure you want to submit your test? You won't be able to make
                any changes after submission. Please review your answers before
                proceeding.
              </AlertDialogDescription>
              
              {hasFailures && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 rounded-md">
                  <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                  <span className="text-sm text-red-700 dark:text-red-400">
                    {failedUploadsCount} {failedUploadsCount === 1 ? 'upload' : 'uploads'} failed. 
                    Some video/audio responses may not be saved.
                  </span>
                </div>
              )}
              
              {!hasFailures && (
                <div className="flex items-center gap-2 p-3 bg-green-500/10 rounded-md">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-green-700 dark:text-green-400">
                    All responses have been saved successfully
                  </span>
                </div>
              )}
            </div>
          )}
        </AlertDialogHeader>
        
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>
            {hasPendingUploads ? "Keep Waiting" : "Go Back"}
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={onConfirm}
            disabled={!canSubmit}
            className={hasFailures ? "bg-orange-500 hover:bg-orange-600" : ""}
          >
            {hasFailures ? "Submit Anyway" : "Submit Test"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
